/**
 * features/finance/Analysis.tsx
 * ------------------------------------------------------------------
 * WHERE THE MONEY COMES FROM, AND WHERE IT GOES.
 *
 * Two questions a head asks that the ledger cannot answer on its own,
 * because a ledger is organised by account and the questions are about
 * groups of pupils and groups of suppliers.
 *
 * BILLED AND COLLECTED SIT SIDE BY SIDE, NEVER BLENDED. A form that
 * bills well and collects badly looks identical to one that bills badly
 * and collects well the moment you average them — and the two need
 * opposite responses. One is a fee-setting problem, the other a
 * collections problem, and a single "income" figure sends the school to
 * fix the wrong one.
 *
 * WHAT WAS GIVEN AWAY IS ITS OWN COLUMN. Netting a discount into the
 * billed figure hides how much of a form's income was forgiven rather
 * than charged, which is a decision the school made and should be able
 * to see.
 *
 * SPENDING IS SHOWN BY WHO WAS PAID as well as by account. "We spent
 * P240,000 on repairs" is a fact; "P180,000 of it went to one
 * contractor" is the beginning of a question.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  ChartPie, TrendingUp, Users, Truck, AlertTriangle,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useData } from '@/contexts/DataContext';
import { db } from '@/lib/firebase';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { useGlData } from '@/features/finance/useGlData';
import { VOUCHER_COLLECTIONS, type Voucher } from '@/lib/vouchers';
import {
  analyseIncome, analyseSpendByPayee, analyseSpendByAccount,
  spendConcentration, monthlyTrend,
} from '@/lib/financeAnalysis';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, ExportBar,
} from '@/features/finance/ui';
import { downloadTablePdf, downloadCsv, type TableExport } from '@/lib/tableExport';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type Grouping = 'form' | 'class';

export default function Analysis() {
  const { students, classes } = useData();
  const fin = useFinanceData();
  const gl = useGlData(`FY${new Date().getFullYear()}`);

  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [grouping, setGrouping] = useState<Grouping>('form');
  const [spendBy, setSpendBy] = useState<'payee' | 'account'>('payee');

  useEffect(() => onSnapshot(
    query(collection(db, VOUCHER_COLLECTIONS.vouchers)),
    (s) => setVouchers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Voucher)),
    () => setVouchers([]),
  ), []);

  /** One function serves both groupings, so they cannot drift apart. */
  const groupOf = useMemo(() => {
    const byStudent = new Map(students.map((s) => [s.id, s.currentClass]));
    const byClass = new Map(classes.map((c) => [c.id, c]));
    return (studentId: string) => {
      const cls = byClass.get(byStudent.get(studentId) ?? '');
      if (!cls) return undefined;
      return grouping === 'form'
        ? { key: cls.form, label: `Form ${cls.form}` }
        : { key: cls.id, label: cls.name };
    };
  }, [students, classes, grouping]);

  const income = useMemo(
    () => analyseIncome(fin.invoices, fin.payments, groupOf),
    [fin.invoices, fin.payments, groupOf],
  );

  const nameOf = (code: string) =>
    gl.accounts.find((a) => a.code === code)?.name ?? code;

  const spend = useMemo(
    () => (spendBy === 'payee'
      ? analyseSpendByPayee(vouchers)
      : analyseSpendByAccount(vouchers, nameOf)),
    [vouchers, spendBy, gl.accounts],
  );

  const concentration = useMemo(
    () => (spendBy === 'payee' ? spendConcentration(spend, 20) : []),
    [spend, spendBy],
  );

  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (11 - i), 1));
      return d.toISOString().slice(0, 7);
    });
  }, []);

  const trend = useMemo(
    () => monthlyTrend(fin.invoices, fin.payments, vouchers, months),
    [fin.invoices, fin.payments, vouchers, months],
  );

  if (fin.loading || gl.loading) {
    return <div className="p-6 flex justify-center"><Spinner /></div>;
  }

  const totals = income.reduce(
    (a, r) => ({
      billed: a.billed + r.billed,
      collected: a.collected + r.collected,
      discounts: a.discounts + r.discounts,
    }),
    { billed: 0, collected: 0, discounts: 0 },
  );
  const overallRate = totals.billed > 0
    ? Math.round((totals.collected / totals.billed) * 1000) / 10
    : 0;

  function exportIncome() {
    const spec: TableExport<typeof income[number]> = {
      title: 'FEE INCOME ANALYSIS',
      meta: `By ${grouping} · billed and collected shown separately`,
      columns: [
        { header: grouping === 'form' ? 'Form' : 'Class', text: (r) => r.label },
        { header: 'Pupils', text: (r) => String(r.pupils), align: 'right' },
        { header: 'Charged', text: (r) => money(r.gross), raw: (r) => r.gross, align: 'right' },
        { header: 'Given away', text: (r) => money(r.discounts), raw: (r) => r.discounts, align: 'right' },
        { header: 'Billed', text: (r) => money(r.billed), raw: (r) => r.billed, align: 'right' },
        { header: 'Collected', text: (r) => money(r.collected), raw: (r) => r.collected, align: 'right' },
        { header: 'Outstanding', text: (r) => money(r.outstanding), raw: (r) => r.outstanding, align: 'right' },
        { header: 'Rate', text: (r) => `${r.collectionRate}%`, raw: (r) => r.collectionRate, align: 'right' },
      ],
      rows: income,
      note: 'The collection rate is measured against what was BILLED, not what was charged '
        + 'gross — a form carrying a bursary has not failed to collect what the school forgave.',
      filename: `fee-income-by-${grouping}`,
    };
    return spec;
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={ChartPie} tone="navy"
        title="Analysis"
        subtitle="Where the money comes from, and where it goes"
      />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <StatChip tone="navy" value={money(totals.billed)} label="Billed" />
        <StatChip tone="emerald" value={money(totals.collected)} label="Collected" />
        <StatChip tone={overallRate >= 90 ? 'emerald' : overallRate >= 70 ? 'amber' : 'rose'}
          value={`${overallRate}%`} label="Collection rate" />
        <StatChip tone="sky" value={money(totals.discounts)} label="Given away" />
      </div>

      <SoftCard pad="p-0" className="mb-5">
        <PanelHeader
          icon={Users} tone="navy" title="Fee income"
          subtitle="Billed and collected side by side — averaging them hides which problem it is"
          right={(
            <div className="flex items-center gap-2">
              <select value={grouping} onChange={(e) => setGrouping(e.target.value as Grouping)}
                className={glassFieldClass('max-w-[130px] text-xs')}>
                <option value="form">By form</option>
                <option value="class">By class</option>
              </select>
              <ExportBar onPdf={() => downloadTablePdf(exportIncome())}
                onCsv={() => downloadCsv(exportIncome())} />
            </div>
          )}
        />
        {income.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">Nothing has been billed yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">{grouping === 'form' ? 'Form' : 'Class'}</th>
                  <th className="px-2 font-semibold text-right">Pupils</th>
                  <th className="px-2 font-semibold text-right">Charged</th>
                  <th className="px-2 font-semibold text-right">Given away</th>
                  <th className="px-2 font-semibold text-right">Billed</th>
                  <th className="px-2 font-semibold text-right">Collected</th>
                  <th className="px-2 pr-4 font-semibold text-right">Rate</th>
                </tr>
              </thead>
              <tbody>
                {income.map((r) => (
                  <tr key={r.key} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="py-2 pl-4 pr-2 font-medium text-gray-900 dark:text-white">
                      {r.label}
                    </td>
                    <td className="px-2 text-right tabular-nums text-gray-500">{r.pupils}</td>
                    <td className="px-2 text-right tabular-nums text-gray-500">{money(r.gross)}</td>
                    <td className="px-2 text-right tabular-nums text-sky-700 dark:text-sky-400">
                      {r.discounts ? money(r.discounts) : ''}
                    </td>
                    <td className="px-2 text-right tabular-nums">{money(r.billed)}</td>
                    <td className="px-2 text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                      {money(r.collected)}
                    </td>
                    <td className={`px-2 pr-4 text-right tabular-nums font-medium ${
                      r.collectionRate >= 90 ? 'text-emerald-700 dark:text-emerald-400'
                        : r.collectionRate >= 70 ? 'text-amber-700 dark:text-amber-300'
                          : 'text-red-700 dark:text-red-400'
                    }`}>
                      {r.collectionRate}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>

      {concentration.length > 0 && (
        <SoftCard className="mb-5 border-l-4 border-sky-400">
          <p className="text-sm text-gray-700 dark:text-gray-200">
            <AlertTriangle size={15} className="inline mr-1 -mt-0.5 text-sky-600" />
            {concentration.map((c) => `${c.label} took ${c.sharePct}% of what the school paid out`)
              .join('; ')}. Not a fault — a school genuinely has one landlord and one bus
            contractor — but it is worth asking once a year whether it should still be so.
          </p>
        </SoftCard>
      )}

      <SoftCard pad="p-0" className="mb-5">
        <PanelHeader
          icon={Truck} tone="navy" title="Spending"
          subtitle="What was actually paid — an approved voucher is a commitment, not spending"
          right={(
            <select value={spendBy} onChange={(e) => setSpendBy(e.target.value as 'payee' | 'account')}
              className={glassFieldClass('max-w-[150px] text-xs')}>
              <option value="payee">By who was paid</option>
              <option value="account">By account</option>
            </select>
          )}
        />
        {spend.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">Nothing has been paid out yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                <th className="py-2 pl-4 pr-2 font-semibold">
                  {spendBy === 'payee' ? 'Paid to' : 'Account'}
                </th>
                <th className="px-2 font-semibold text-right">Payments</th>
                <th className="px-2 font-semibold text-right">Total</th>
                <th className="px-2 pr-4 font-semibold text-right">Share</th>
              </tr>
            </thead>
            <tbody>
              {spend.slice(0, 30).map((r) => (
                <tr key={r.key} className="border-b border-gray-50 dark:border-gray-800">
                  <td className="py-2 pl-4 pr-2 text-gray-800 dark:text-gray-100">{r.label}</td>
                  <td className="px-2 text-right tabular-nums text-gray-500">{r.count}</td>
                  <td className="px-2 text-right tabular-nums font-medium">{money(r.total)}</td>
                  <td className="px-2 pr-4 text-right tabular-nums text-gray-500">{r.sharePct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SoftCard>

      <SoftCard pad="p-0">
        <PanelHeader icon={TrendingUp} tone="navy" title="The last twelve months"
          subtitle="Billed, collected and spent on one timeline" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                <th className="py-2 pl-4 pr-2 font-semibold">Month</th>
                <th className="px-2 font-semibold text-right">Billed</th>
                <th className="px-2 font-semibold text-right">Collected</th>
                <th className="px-2 pr-4 font-semibold text-right">Spent</th>
              </tr>
            </thead>
            <tbody>
              {trend.map((p) => (
                <tr key={p.month} className="border-b border-gray-50 dark:border-gray-800">
                  <td className="py-2 pl-4 pr-2 tabular-nums text-gray-600 dark:text-gray-300">
                    {p.month}
                  </td>
                  <td className="px-2 text-right tabular-nums">{p.billed ? money(p.billed) : ''}</td>
                  <td className="px-2 text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                    {p.collected ? money(p.collected) : ''}
                  </td>
                  <td className="px-2 pr-4 text-right tabular-nums text-red-700 dark:text-red-400">
                    {p.spent ? money(p.spent) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SoftCard>
    </div>
  );
}
